import {
  type EntityAdapter,
  type EntityState,
  type ReducerCreators,
  type SerializedError,
  type ThunkDispatch,
  type UnknownAction,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { getId } from './resourceStoreTypes';

export interface QcsResource {
  clientId?: string;
  id?: string;
  createdAt?: string;
}
export interface QcsResourceSlice<T extends QcsResource = QcsResource> extends EntityState<T, string> {
  loadPending?: boolean;
  isLoading?: boolean;
  error?: SerializedError | null;
  lastSyncDate?: string | null;
}

export const commonSelectors = {
  getLoadPending: (s: QcsResourceSlice) => s.loadPending,
  getIsLoading: (s: QcsResourceSlice) => s.isLoading,
  getError: (s: QcsResourceSlice) => s.error,
};

interface QcsApi<T, RemoteT = T & { id: string; clientId?: string }> {
  create(item: T): Promise<{ resource: RemoteT; clientId?: string; id: string }>;
  update(item: Partial<T> & { id: string }): Promise<{ resource: RemoteT; id: string }>;
  delete(id: string): Promise<void>;
  fetch(): Promise<ReadonlyArray<RemoteT>>;
}

export const createResourceEntityAdapter = <T extends QcsResource>() =>
  createEntityAdapter<T, string>({
    selectId: getId,
    sortComparer: (a, b) => ((a.createdAt ?? '') > (b.createdAt ?? '') ? 1 : -1),
  });

export const createQcsThunks = <T extends QcsResource>({
  create,
  api,
  adapter,
  log,
  refetch,
}: {
  create: ReducerCreators<QcsResourceSlice<T>>;
  api: QcsApi<T>;
  adapter: EntityAdapter<T, string>;
  log?: {
    debug: (...args: any[]) => void;
    error: (...args: any[]) => void;
  }; // TODO: convert loggers to TS
  refetch: (dispatch: ThunkDispatch<unknown, unknown, UnknownAction>) => void;
}) => ({
  create: create.asyncThunk(
    async (item: T & { clientId: string }, thunkApi) => {
      log?.debug('Creating item', item);
      try {
        return await api.create(item);
      } catch (e) {
        refetch(thunkApi.dispatch);
        throw e;
      }
    },
    {
      pending(state, action) {
        state.isLoading = true;
        adapter.addOne(state, action.meta.arg);
      },
      rejected(state, action) {
        log?.error('Received error', action.error);
        state.error = action.error;
      },
      fulfilled(state, action) {
        log?.debug('Creating item, response', action.payload);
        if (action.payload.clientId && state.ids.includes(action.payload.clientId)) {
          adapter.updateOne(state, {
            id: action.payload.clientId ?? action.payload.resource.clientId,
            changes: action.payload.resource,
          });
        } else {
          adapter.addOne(state, action.payload.resource);
        }
      },
      settled(state) {
        state.isLoading = false;
      },
    },
  ),
  fetch: create.asyncThunk(
    async (_: void) => {
      log?.debug('Getting items');
      return await api.fetch();
    },
    {
      pending(state) {
        state.isLoading = true;
      },
      fulfilled(state, action) {
        log?.debug('Getting items, response', action.payload);
        adapter.setAll(state, action.payload);
        state.error = undefined;
      },
      rejected(state, action) {
        log?.error('Received error', action.error);
        state.error = action.error;
      },
      settled(state) {
        state.isLoading = false;
        state.loadPending = false;
      },
    },
  ),
  update: create.asyncThunk(
    async (item: Partial<T> & { id: string }, thunkApi) => {
      log?.debug('Updating item', item);
      try {
        return await api.update(item);
      } catch (e) {
        refetch(thunkApi.dispatch);
        throw e;
      }
    },
    {
      pending(state, action) {
        state.isLoading = true;
        adapter.updateOne(state, { id: getId(action.meta.arg), changes: action.meta.arg });
      },
      rejected(state, action) {
        log?.error('Received error', action.error);
        state.error = action.error;
      },
      fulfilled(state, action) {
        log?.debug('Updating item, response', action.payload);
        adapter.setOne(state, action.payload.resource);
      },
      settled(state) {
        state.isLoading = false;
      },
    },
  ),
  delete: create.asyncThunk(
    async (id: string, thunkApi) => {
      log?.debug('Deleting item', id);
      try {
        await api.delete(id);
      } catch (e) {
        refetch(thunkApi.dispatch);
        throw e;
      }
    },
    {
      pending(state, action) {
        state.isLoading = true;
        adapter.removeOne(state, action.meta.arg);
      },
      fulfilled() {
        log?.debug('Delete item successful');
      },
      rejected(state, action) {
        log?.error('Received error', action.error);
        state.error = action.error;
      },
      settled(state) {
        state.isLoading = false;
        state.loadPending = false;
      },
    },
  ),
});

export const retryAsync = async <R>(asyncFunc: () => Promise<R>, retries = 3, delay?: number): Promise<R> => {
  try {
    return await asyncFunc();
  } catch (error) {
    if (retries === 1) throw error;
    if (delay) {
      await new Promise((res) => setTimeout(res, delay));
    }
    return await retryAsync(asyncFunc, retries - 1);
  }
};
